/* eslint @typescript-eslint/strict-boolean-expressions:0 */
// import log from 'loglevel'
import {
  compact,
  concat,
  endsWith,
  flatten,
  isNil,
  isNumber,
  map,
  max,
  min,
  size,
  template,
  toString
} from 'lodash/fp'
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  eachMinuteOfIntervalWithOptions,
  eachMonthOfInterval,
  format,
  getDate,
  getDay,
  getYear,
  parseISO
} from 'date-fns/fp'
import { endOfMonth } from 'date-fns'
import axios from 'axios'
import { type ChartConfiguration, type ChartTypeRegistry } from 'chart.js'
import log from 'loglevel'
import { type ConfigProps, hasConfig } from 'components/Dashboard'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const defaultBorderColors = [
  ['#EA3324'],
  ['#5C97CE'],
  ['rgba(0, 230, 0, 1)'],
  ['#34692E'],
  ['#71ba17ff'],
  ['#D6FEA4'],
  ['#FFFF55'],
  ['#FFFFD1']
]
const times = map((date: Date) => format('H:mm')(date))(
  eachMinuteOfIntervalWithOptions({ step: 30 })({
    start: parseISO('1970-01-01T00:00'),
    end: parseISO('1970-01-01T23:30')
  })
)
const valueLabel = (classification: string | null | undefined): string =>
  isNil(classification) || classification === '供給' ? '電気使用量' : '売電量'
const yTitle = (
  classification: string | null | undefined
): Record<string, string | boolean> => ({
  display: true,
  text: `${valueLabel(classification)}`,
  color: 'black'
})

const holidaysUrl = 'https://holidays-jp.github.io/api/v1/<%= year %>/date.json'
const dayStrings = ['日', '月', '火', '水', '木', '金', '土']
// <block:plugin:0>
const getOrCreateLegendList = (
  _chart: any,
  id: string
): HTMLUListElement | null => {
  const legendContainer = document.getElementById(id)
  // legendContainer?.setAttribute('style', "background-color:'rgb(179, 230, 244)';")
  if (isNil(legendContainer)) return null
  legendContainer.style.background = 'rgba(179,230,244, 1)'
  legendContainer.style.padding = '1em'
  let listContainer = legendContainer.querySelector('ul')
  if (!listContainer) {
    listContainer = document.createElement('ul')
    listContainer.style.display = 'flex'
    // listContainer.style.flexDirection = 'column'
    listContainer.style.flexDirection = 'row'
    listContainer.style.flexWrap = 'wrap'
    listContainer.style.margin = '0'
    listContainer.style.padding = '0'
    legendContainer.appendChild(listContainer)
  }
  return listContainer
}

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart: any, _args: any, options: any): void {
    const ul = getOrCreateLegendList(chart, options.containerID)
    if (isNil(ul)) return
    while (!isNil(ul.firstChild)) {
      ul.firstChild.remove()
    }
    for (const item of chart.options.plugins.legend.labels.generateLabels(
      chart
    )) {
      if (isNil(item.text)) continue
      const li = document.createElement('li')
      li.style.alignItems = 'center'
      li.style.cursor = 'pointer'
      li.style.display = 'flex'
      li.style.flexDirection = 'row'
      li.style.marginLeft = '10px'
      const boxSpan = document.createElement('span')
      boxSpan.style.background = item.strokeStyle
      boxSpan.style.borderColor = item.strokeStyle
      boxSpan.style.borderWidth = `${item.lineWidth as number}px`
      boxSpan.style.display = 'inline-block'
      boxSpan.style.flexShrink = '0'
      boxSpan.style.height = '12px'
      boxSpan.style.marginRight = '10px'
      boxSpan.style.width = '12px'
      const textContainer = document.createElement('p')
      textContainer.style.color = item.strokeStyle
      textContainer.style.margin = '0'
      textContainer.style.padding = '0'
      textContainer.style.fontSize = '12px'
      textContainer.style.color = 'rgb(0,0,0)'
      textContainer.style.textDecoration = item.hidden ? 'line-through' : ''
      const text = document.createTextNode(item.text)
      textContainer.appendChild(text)
      li.appendChild(boxSpan)
      li.appendChild(textContainer)
      ul.appendChild(li)
    }
  }
}
const legendLine = (enable: boolean): any =>
  enable
    ? {
        fullSize: false,
        align: 'start',
        labels: {
          usePointStyle: true,
          generateLabels: (chart: any) =>
            !isNil(chart.data)
              ? // @ts-expect-error ts(2339)
                map.convert({ cap: false })((item: any, index: number) => {
                  return {
                    text: item.label,
                    fillStyle: 'rgb(255,255,255)',
                    strokeStyle: item.borderColor[0],
                    fontColor: item.borderColor[0],
                    lineWidth: 3,
                    lineDash: item.borderDash,
                    pointStyle: 'line',
                    datasetIndex: index,
                    hidden: !(chart.isDatasetVisible(index) as boolean)
                  }
                })(chart.data.datasets)
              : null
        }
      }
    : { display: false }
const legendBar = (enable: boolean): any =>
  enable
    ? {
        labels: {
          usePointStyle: true,
          generateLabels: (chart: any) =>
            !isNil(chart.data)
              ? // @ts-expect-error ts(2339)
                map.convert({ cap: false })((item: any, index: number) => {
                  return {
                    text: item.label,
                    fillStyle: item.borderColor[0],
                    strokeStyle: item.borderColor[0],
                    fontColor: item.borderColor[0],
                    pointStyle: 'rect',
                    datasetIndex: index,
                    hidden: !(chart.isDatasetVisible(index) as boolean)
                  }
                })(chart.data.datasets)
              : null
        }
      }
    : { display: false }
const tempatureAnnotation = (
  x: number,
  y: number,
  content: string[] | undefined,
  configChart: ConfigProps
): { annotations: Record<string, any> } => ({
  annotations:
    !isNil(content) && size(content) === 2
      ? {
          label1: {
            type: 'label',
            position: { x: 'end', y: 'start' },
            xValue: x,
            yValue: y,
            backgroundColor: hasConfig(configChart, 'colors.dailyAnnotation')
              ? configChart.colors.dailyAnnotation
              : 'rgb(235, 205, 179)',
            content,
            font: {
              size: 12
            }
          }
        }
      : {}
})
const createTempatureAnnotation = (
  tempature:
    | {
        date: string | undefined
        maxTemp: number | null | undefined
        minTemp: number | null | undefined
        type?: string
      }
    | undefined,
  compareTo?: string
): any | undefined => {
  log.debug({ compareTo })
  if (isNil(tempature)) return undefined
  const { date, maxTemp, minTemp, type } = tempature
  if (isNil(date) || isNil(maxTemp) || isNil(minTemp)) return undefined
  const prefix = !isNil(type) && type !== 'daily' ? '平均' : ''
  return `${format(
    isNil(type) || type === 'daily'
      ? isNil(compareTo) || compareTo === '前週同曜日'
        ? 'M月d日'
        : compareTo === '前年同月平均'
        ? 'yyyy年M月平均'
        : 'yyyy年平均'
      : type === 'monthly'
      ? 'yyyy年M月'
      : 'yyyy年'
  )(
    parseISO(date)
  )} ${prefix}最高気温 ${maxTemp}°C ${prefix}最低気温 ${minTemp}°C`
}
const backgroundColorPlugin = {
  id: 'customCanvasBackgroundColor',
  beforeDraw: (chart, _args, options) => {
    const { ctx } = chart
    ctx.save()
    ctx.globalCompositeOperation = 'destination-over'
    ctx.fillStyle = options.color || '#99ffff'
    ctx.fillRect(0, 0, chart.width, chart.height)
    ctx.restore()
  }
}
const floorValues = (values: Array<number | null>): Array<number | null> =>
  map((value: number | null) =>
    isNil(value) ? value : Math.floor(value * 10) / 10
  )(values)
export const createDailyChart = (
  values: Array<number | null>,
  lastValues: Array<number | null>,
  additionalValues: Array<number | null> | undefined,
  targetsValues:
    | Array<{ key: string; data: Array<number | null> | null } | undefined>
    | undefined,
  tempature: {
    date: string
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
  },
  lastTempature: {
    date: string | undefined
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
    type: string
  },
  compareTo: string,
  configChart: ConfigProps,
  classification: string | null | undefined
): ChartConfiguration => {
  const yMinValue = configChart.yMinValue
  const yMax = max(concat(values)(lastValues)) as number
  const xMax = max([size(values), size(lastValues)]) as number
  const yMin = yMinValue ?? (min(concat(values)(lastValues)) as number)
  // const margin = 150 / (320 / (yMax - yMin))
  const stepSize = (yMax - yMin) / 5
  const content = compact([
    createTempatureAnnotation(tempature),
    createTempatureAnnotation(lastTempature, compareTo)
  ])
  const margin = size(content) < 2 ? 0 : stepSize
  const annotationY =
    yMax +
    margin +
    ((yMax + margin) % stepSize === 0
      ? 0
      : stepSize - ((yMax + margin) % stepSize))
  const above = hasConfig(configChart, 'colors.above')
    ? configChart.colors.above
    : 'rgb(150, 200, 200)'
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? configChart.colors.borderColors
    : defaultBorderColors
  const options: ChartConfiguration = {
    type: 'line' as keyof ChartTypeRegistry,
    data: {
      labels: times,
      datasets: compact(
        flatten([
          {
            label: `${valueLabel(classification)}`,
            data: values,
            fill: {
              target: '1',
              above,
              below: 'rgb(0, 0, 0, 0)'
            },
            borderColor: borderColors[0],
            borderWidth: 4,
            tension: 0.1
          },
          {
            label: `${compareTo}`,
            data: lastValues,
            borderColor: borderColors[1],
            borderWidth: 4,
            tension: 0.1
          },
          isNil(additionalValues)
            ? undefined
            : {
                label: `当月平均`,
                data: additionalValues,
                borderDash: [5, 5],
                borderWidth: 2,
                borderColor: borderColors[2],
                tension: 0.1
              },
          isNil(targetsValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (
                  targetValues:
                    | { key: string; data: Array<number | null> }
                    | undefined,
                  index: number
                ) => {
                  const { key, data } = targetValues ?? {
                    key: '',
                    data: undefined
                  }
                  return isNil(data)
                    ? {}
                    : {
                        label: `${key}平均`,
                        data,
                        borderDash: [5, 5],
                        borderWidth: 2,
                        borderColor: borderColors[3 + index],
                        tension: 0.1
                      }
                }
              )(targetsValues)
        ])
      )
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        y: {
          suggestedMax: yMax + margin,
          suggestedMin: yMin,
          title: yTitle(classification),
          ticks: {
            stepSize
          }
        }
      },
      plugins: {
        htmlLegend: isNil(targetsValues)
          ? {}
          : {
              containerID: 'legend-container'
            },
        legend: legendLine(isNil(targetsValues)),
        annotation: tempatureAnnotation(
          xMax - 1,
          annotationY,
          content,
          configChart
        ),
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? configChart.colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: false
        }
      }
    },
    plugins: [backgroundColorPlugin, htmlLegendPlugin]
  }
  return options
}
export const createWeeklyChart = async (
  date: string,
  values: Array<number | null>,
  lastValues: Array<number | null>,
  compareTo: string,
  configChart: ConfigProps,
  classification: string | null | undefined
): Promise<ChartConfiguration> => {
  const { data: holidays } = await axios.get(
    template(holidaysUrl)({ year: getYear(parseISO(date)) })
  )
  const duration = map((date: Date) => {
    const day = getDate(date)
    return [dayStrings[getDay(date)], day]
  })(
    eachDayOfInterval({
      start: parseISO(date),
      end: addDays(6)(parseISO(date))
    })
  )
  const above = hasConfig(configChart, 'colors.above')
    ? configChart.colors.above
    : 'rgb(150, 200, 200)'
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? configChart.colors.borderColors
    : [['rgba(75, 192, 192)'], ['rgba(192, 192, 75)']]
  const dayColors = {
    日: 'red',
    月: '',
    火: '',
    水: '',
    木: '',
    金: '',
    土: 'blue'
  }
  const options: ChartConfiguration = {
    type: 'line' as keyof ChartTypeRegistry,
    data: {
      labels: duration,
      datasets: [
        {
          label: `${compareTo}の${valueLabel(classification)}`,
          data: floorValues(lastValues),
          borderDash: [5, 5],
          borderColor: borderColors[1],
          tension: 0.1
        },
        {
          label: `${valueLabel(classification)}`,
          data: floorValues(values),
          fill: {
            target: '1',
            above,
            below: 'rgb(0, 0, 0, 0)'
          },
          borderColor: borderColors[0],
          tension: 0.1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: (context: any) => {
              const day = `${format('yyyy-MM')(parseISO(date))}-${(
                '0' + toString((context.tick.value as number) + 1)
              ).slice(-2)}`
              if (!isNil(holidays[day])) {
                return 'red'
              } else {
                return dayColors[
                  context.tick.label[0] as keyof typeof dayColors
                ]
              }
            }
          }
        },
        y: {
          title: yTitle(classification)
        }
      },
      plugins: {
        legend: legendLine,
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? configChart.colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: false
        }
      }
    },
    plugins: [backgroundColorPlugin]
  }
  return options
}

export const createMonthlyChart = async (
  date: string,
  values: Array<number | null>,
  lastValues: Array<number | null>,
  targetsValues:
    | Array<{ key: string; data: Array<number | null> | null } | undefined>
    | undefined,
  tempature: {
    date: string
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
  },
  lastTempature: {
    date: string | undefined
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
    type: string
  },
  compareTo: string,
  configChart: ConfigProps,
  classification: string | null | undefined
): Promise<ChartConfiguration> => {
  const yMinValue = configChart.yMinValue
  const yMax = max(concat(values)(lastValues)) as number
  const xMax = max([size(values), size(lastValues)]) as number
  const yMin = yMinValue ?? (min(concat(values)(lastValues)) as number)
  // const margin = 150 / (320 / (yMax - yMin))
  const stepSize = (yMax - yMin) / 5
  const margin = stepSize
  const annotationY =
    yMax +
    margin +
    ((yMax + margin) % stepSize === 0
      ? 0
      : stepSize - ((yMax + margin) % stepSize))
  const content = compact([
    createTempatureAnnotation({ ...tempature, type: 'monthly' }),
    createTempatureAnnotation(lastTempature)
  ])
  const { data: holidays } = await axios.get(
    template(holidaysUrl)({ year: getYear(parseISO(date)) })
  )
  const duration = map((date: Date) => {
    const day = getDate(date)
    return [dayStrings[getDay(date)], day]
  })(
    eachDayOfInterval({
      start: parseISO(date),
      end: endOfMonth(parseISO(date))
    })
  )
  const above = hasConfig(configChart, 'colors.above')
    ? configChart.colors.above
    : 'rgb(150, 200, 200)'
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? configChart.colors.borderColors
    : defaultBorderColors
  const dayColors = {
    日: 'red',
    月: '',
    火: '',
    水: '',
    木: '',
    金: '',
    土: 'blue'
  }
  const options: ChartConfiguration = {
    type: 'line' as keyof ChartTypeRegistry,
    data: {
      labels: duration,
      datasets: compact(
        flatten([
          {
            label: `${valueLabel(classification)}`,
            data: values,
            fill: {
              target: '1',
              above,
              below: 'rgb(0, 0, 0, 0)'
            },
            borderColor: borderColors[0],
            borderWidth: 4,
            tension: 0.1
          },
          {
            label: `${compareTo}`,

            data: lastValues,
            borderColor: borderColors[1],
            borderWidth: 4,
            tension: 0.1
          },
          isNil(targetsValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (
                  targetValues:
                    | { key: string; data: Array<number | null> }
                    | undefined,
                  index: number
                ) => {
                  const { key, data } = targetValues ?? {
                    key: '',
                    data: undefined
                  }
                  return isNil(data)
                    ? {}
                    : {
                        label: `${key}平均`,
                        data,
                        borderDash: [5, 5],
                        borderWidth: 2,
                        borderColor: borderColors[3 + index],
                        tension: 0.1
                      }
                }
              )(targetsValues)
        ])
      )
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: (context: any) => {
              const day = `${format('yyyy-MM')(parseISO(date))}-${(
                '0' + toString((context.tick.value as number) + 1)
              ).slice(-2)}`
              if (!isNil(holidays[day])) {
                return 'red'
              } else {
                return dayColors[
                  context.tick.label[0] as keyof typeof dayColors
                ]
              }
            }
          }
        },
        y: {
          suggestedMin: yMin,
          title: yTitle(classification),
          ticks: {
            stepSize
          }
        }
      },
      plugins: {
        htmlLegend: isNil(targetsValues)
          ? {}
          : {
              containerID: 'legend-container'
            },
        legend: legendLine(isNil(targetsValues)),
        annotation: tempatureAnnotation(
          xMax - 1,
          annotationY,
          content,
          configChart
        ),
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? configChart.colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: false
        }
      }
    },
    plugins: [backgroundColorPlugin, htmlLegendPlugin]
  }
  return options
}
export const createMonthlyHorizontalChart = async (
  values: Array<number | null>,
  lastValues: Array<number | null>,
  compareTo: string,
  configChart: ConfigProps,
  classification: string | null | undefined
): Promise<ChartConfiguration> => {
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? configChart.colors.borderColors
    : [['rgba(75, 192, 192)'], ['rgba(192, 192, 75)']]
  const options: ChartConfiguration = {
    type: 'bar' as keyof ChartTypeRegistry,
    data: {
      labels: [''],
      datasets: [
        {
          label: `${valueLabel(classification)}`,
          data: floorValues(values),
          borderColor: borderColors[0],
          backgroundColor: borderColors[0],
          tension: 0.1
        },
        {
          label: `${compareTo}の${valueLabel(classification)}`,
          data: floorValues(lastValues),
          borderColor: borderColors[1],
          backgroundColor: borderColors[1],
          tension: 0.1
        }
      ]
    },
    options: {
      indexAxis: 'y',
      scales: {
        x: {
          grid: {
            display: false
          }
        }
      },
      plugins: {
        legend: legendBar,
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? configChart.colors.canvas
            : 'rgb(179, 230, 244)'
        }
      }
    },
    plugins: [backgroundColorPlugin]
  }
  return options
}

export const createYearlyChart = (
  date: string,
  values: Array<number | null>,
  lastValues: Array<number | null>,
  targetsValues:
    | Array<{ key: string; data: Array<number | null> | null } | undefined>
    | undefined,
  tempature: {
    date: string
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
  },
  lastTempature: {
    date: string | undefined
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
    type: string
  },
  compareTo: string,
  configChart: ConfigProps,
  classification: string | null | undefined,
  showValue: boolean = true
): ChartConfiguration => {
  const yMinValue = configChart.yMinValue
  const yMax = max(concat(values)(lastValues)) as number
  const xMax = max([size(values), size(lastValues)]) as number
  const yMin = yMinValue ?? (min(concat(values)(lastValues)) as number)
  // const margin = 150 / (320 / (yMax - yMin))
  const stepSize = (yMax - yMin) / 5
  const margin = stepSize
  const annotationY =
    yMax +
    margin +
    ((yMax + margin) % stepSize === 0
      ? 0
      : stepSize - ((yMax + margin) % stepSize))
  const content = compact([
    createTempatureAnnotation({ ...tempature, type: 'yearly' }),
    createTempatureAnnotation(lastTempature)
  ])
  const duration = map((date: Date) => format('M月')(date))(
    eachMonthOfInterval({
      start: parseISO(date),
      end: addMonths(11)(parseISO(date))
    })
  )
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? configChart.colors.borderColors
    : defaultBorderColors
  log.debug({ values })
  const options: ChartConfiguration = {
    type: 'bar' as keyof ChartTypeRegistry,
    data: {
      labels: duration,
      datasets: compact(
        flatten([
          {
            label: `${valueLabel(classification)}`,
            data: map((item: any) => (!isNumber(item) ? null : item))(values),
            borderColor: borderColors[0],
            backgroundColor: borderColors[0],
            tension: 0.1,
            datalabels: {
              anchor: 'start',
              color: borderColors[0],
              align: 'end',
              offset: 20,
              backgroundColor: 'rgb(255, 255, 255)',
              formatter: (value: number | null) =>
                !isNil(value) ? Math.floor(value).toLocaleString() : null
            }
          },
          {
            label: `${compareTo}の${valueLabel(classification)}`,
            data: map((item: any) => (!isNumber(item) ? null : item))(
              lastValues
            ),
            borderColor: borderColors[1],
            backgroundColor: borderColors[1],
            tension: 0.1,
            datalabels: {
              anchor: 'end',
              color: borderColors[1],
              align: 'start',
              offset: 20,
              backgroundColor: 'rgb(255, 255, 255)',
              formatter: (value: number | null) =>
                !isNil(value) ? Math.floor(value).toLocaleString() : null
            }
          },
          isNil(targetsValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (
                  targetValues:
                    | { key: string; data: Array<number | null> }
                    | undefined,
                  index: number
                ) => {
                  const { key, data } = targetValues ?? {
                    key: '',
                    data: undefined
                  }
                  return isNil(data)
                    ? {}
                    : {
                        label: `${key}の${compareTo}${
                          !endsWith('平均')(compareTo) ? '平均' : ''
                        }`,
                        data: map((item: any) =>
                          !isNumber(item) ? null : item
                        )(data),
                        borderColor: borderColors[3 + index],
                        backgroundColor: borderColors[3 + index],
                        tension: 0.1,
                        datalabels: {
                          display: false
                        }
                      }
                }
              )(targetsValues)
        ])
      )
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        y: {
          suggestedMin: yMin,
          title: yTitle(classification),
          ticks: {
            stepSize
          }
        }
      },
      plugins: {
        htmlLegend: isNil(targetsValues)
          ? {}
          : {
              containerID: 'legend-container'
            },
        legend: legendBar(isNil(targetsValues)),
        annotation: tempatureAnnotation(
          xMax - 1,
          annotationY,
          content,
          configChart
        ),
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? configChart.colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: showValue,
          formatter: (value: number) => Math.floor(value).toLocaleString()
        }
      }
    },
    plugins: [backgroundColorPlugin, htmlLegendPlugin]
  }
  return options
}
